import type {
  CSSProperties,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
  RefObject,
} from 'react';
import { ResponsiveContainer } from 'recharts';
import styles from './ChartFrame.module.css';
import type { ChartLegendPlacement } from './chartTheme';

/**
 * The box a chart is drawn in: it owns the height, the axis titles, where the
 * legend sits, and the two states where there is no chart to draw.
 *
 * Figma: `_X-axis label` (421:47612) and `_Y-axis label` (421:47614) — the axis
 * *titles*, not the tick labels. Easy to mix up: the tick labels live inside
 * `_X-axis` and `_Y-axis line` and are drawn by Recharts; these two are single
 * strings that sit outside the plot. They are rendered as DOM rather than SVG
 * text so their type comes from CSS, where stylelint can see it.
 *
 * Not in Figma: the loading and empty states, the spacing around titles and
 * legend, and the `role="img"` labelling. The file draws a populated chart only.
 * See plan.md §7 item 132.
 */
export type ChartFrameProps = {
  /**
   * Plot height in px, excluding axis titles and legend. Figma's `Chart` slot is
   * 224 tall, which is the default. A number rather than a token because Figma
   * defines no height variables — the same exception every other component here
   * takes.
   *
   * It has been 269, then 278, and 224 since the 2026-09-16 update redrew
   * `Card`'s slots as 36 / 224 / 12 and grouped the first two into a
   * `KPI+Chart Wrapper` on a `space/24` gap. This default tracks that slot
   * deliberately: when it was left behind at 269 every chart sat 9px short of
   * the box it has to fill, and the same would happen in reverse — 54px of
   * overflow — if it stayed at 278 now.
   *
   * `'fill'` takes the height of the slot the frame is in instead of naming one.
   * `Card`'s `State=2 charts` variant (918:35613) splits its chart area between
   * two plots, so neither can know its own height — a number there would
   * overflow the slot or leave it short, and only the card knows the answer.
   */
  height?: number | 'fill';
  /** Figma `_X-axis label` — the horizontal axis title, centred under the plot. */
  xAxisTitle?: string;
  /** Figma `_Y-axis label` — the vertical axis title, reading bottom to top. */
  yAxisTitle?: string;
  /** Rendered beside or below the plot. Omit to draw no legend. */
  legend?: ReactNode;
  /** Where `legend` goes. Ignored when there is no legend. */
  legendPlacement?: Exclude<ChartLegendPlacement, 'none'>;
  /**
   * Content centred over the plot, above the marks but not capturing pointer
   * events — the donut's centre readout. Anything here must be non-interactive,
   * or it will steal the hover the chart needs.
   */
  overlay?: ReactNode;
  /**
   * The anchored hover readout, drawn over the plot rather than inside the SVG.
   *
   * Recharts' own tooltip is positioned by the pointer, which is exactly what
   * `useAnchoredTooltip` replaces — so the bubble comes through here instead,
   * into `.plot`, which is the positioning context its `--anchor-x`/`--anchor-y`
   * resolve against. `CalendarHeatmap` and `WordCloud` have always drawn theirs
   * this way; this is the same arrangement reaching the Recharts charts.
   *
   * Unlike `overlay` this may be interactive — a locked readout carries a
   * button. The layer itself never is: see `.hoverLayer`.
   */
  hover?: ReactNode;
  /**
   * The plot box, which `useAnchoredTooltip` measures to decide which side of a
   * point has room for the readout.
   */
  plotRef?: RefObject<HTMLDivElement | null>;
  /**
   * Keyboard access to the plot, for a chart whose points can be opened. A
   * pointer is how everyone else anchors the readout; arrow keys are the only
   * way in without one.
   *
   * It makes a `role="img"` focusable, which is a compromise rather than a
   * finding: the label a chart needs from `img` is the same string a stepping
   * reader needs on focus, and splitting them across two elements announced the
   * chart twice. Flagged rather than silently settled — plan.md §7 item 132.
   */
  plotProps?: {
    tabIndex?: number;
    onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
    onBlur?: FocusEventHandler<HTMLDivElement>;
  };
  /**
   * Squares the plot to `height` instead of letting it fill the row. Set it for
   * a radial chart: Recharts sizes a pie at `min(width, height)` and centres it,
   * so a plot wider than it is tall carries the surplus as dead space around the
   * ring — which the eye reads as the gap before the legend. Wrong for cartesian
   * charts, where a wide plot is the point.
   */
  squarePlot?: boolean;
  /** Swaps the plot for a loading message and marks the region busy. */
  loading?: boolean;
  /** Swaps the plot for an empty message. Ignored while `loading`. */
  empty?: boolean;
  /** Shown when `empty`. */
  emptyLabel?: ReactNode;
  /** Shown when `loading`. */
  loadingLabel?: ReactNode;
  /**
   * Accessible name for the plot. A chart is opaque to assistive tech without
   * one — this is the floor, not a substitute for the data in text form.
   * Not in Figma.
   */
  label?: string;
  /** The Recharts chart element. */
  children: ReactNode;
  className?: string;
};

export function ChartFrame({
  height = 224,
  xAxisTitle,
  yAxisTitle,
  legend,
  legendPlacement = 'bottom',
  overlay,
  hover,
  plotRef,
  plotProps,
  squarePlot = false,
  loading = false,
  empty = false,
  emptyLabel = 'No data',
  loadingLabel = 'Loading chart…',
  label,
  children,
  className,
}: ChartFrameProps) {
  const fill = height === 'fill';
  const fixedHeight = fill ? undefined : (height as number);

  const state = loading ? 'loading' : empty ? 'empty' : 'chart';
  const hasLegend = legend !== undefined && legend !== null && legend !== false;

  return (
    <div
      className={[
        styles.root,
        hasLegend && legendPlacement === 'right' ? styles.legendRight : styles.legendBottom,
        squarePlot && styles.centerGroup,
        fill && styles.fillRoot,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/*
       * The cap is the plot's own height, which only this component knows, so it
       * arrives as a custom property rather than a class — the alternative is a
       * stylesheet rule per height a caller might pass.
       */}
      <div
        className={[styles.frame, squarePlot && styles.squareFrame].filter(Boolean).join(' ')}
        style={
          squarePlot && fixedHeight !== undefined
            ? ({ '--chart-plot-size': `${fixedHeight}px` } as CSSProperties)
            : undefined
        }
      >
        {yAxisTitle !== undefined && (
          <div className={styles.yTitle}>
            <span className={styles.title}>{yAxisTitle}</span>
          </div>
        )}

        <div
          ref={plotRef}
          className={[styles.plot, fill && styles.fillPlot].filter(Boolean).join(' ')}
          /*
           * `ResponsiveContainer` measures this box, so a filling plot must get
           * its height from the flex row rather than from an inline style —
           * see `.fillPlot`.
           */
          style={fill ? undefined : { height: fixedHeight }}
          role="img"
          aria-label={label}
          aria-busy={loading || undefined}
          {...(state === 'chart' ? plotProps : undefined)}
        >
          {state === 'chart' ? (
            <>
              {/*
               * `ResponsiveContainer` measures its parent, so the height has to
               * be on `.plot` rather than here — a percentage of an auto-height
               * parent resolves to zero and the chart silently disappears.
               */}
              <ResponsiveContainer width="100%" height="100%">
                {children}
              </ResponsiveContainer>
              {overlay !== undefined && <div className={styles.overlay}>{overlay}</div>}
              {hover !== undefined && hover !== null && (
                <div className={styles.hoverLayer}>{hover}</div>
              )}
            </>
          ) : (
            <p className={styles.message}>{state === 'loading' ? loadingLabel : emptyLabel}</p>
          )}
        </div>

        {xAxisTitle !== undefined && (
          <div className={styles.xTitle}>
            <span className={styles.title}>{xAxisTitle}</span>
          </div>
        )}
      </div>

      {hasLegend && state === 'chart' && <div className={styles.legendSlot}>{legend}</div>}
    </div>
  );
}
